import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { usePartyWithDetails } from '@/hooks/usePartyWithDetails'
import { usePartyDistrictStatesWithDistricts } from '@/hooks/usePartyDistrictStatesWithDistricts'
import { usePartyLeader } from '@/hooks/usePartyLeader'
import { useCandidatesByParty } from '@/hooks/useCandidatesByParty'
import { useWorld } from '@/hooks/useWorld'
import { useFactionsByParty } from '@/hooks/useFactionsByParty'
import { useLastCompletedElection } from '@/hooks/useLastCompletedElection'
import { useUpcomingElection } from '@/hooks/useUpcomingElection'
import { ElectionCountdown } from '@/components/election/ElectionCountdown'
import { PartyHeader } from '@/components/party/PartyHeader'
import { ResourcesBar } from '@/components/party/ResourcesBar'
import { TickStatus } from '@/components/tick'
import { PartyIdeologySection } from '@/components/party/PartyIdeologySection'
import { PartyLeaderCard } from '@/components/party/PartyLeaderCard'
import { NoLeaderWarning } from '@/components/party/NoLeaderWarning'
import { EditPartyModal } from '@/components/party/EditPartyModal'
import { RecruitmentPanel } from '@/components/candidate/RecruitmentPanel'
import { CandidateList } from '@/components/candidate/CandidateList'
import { CandidateDetailModal } from '@/components/candidate/CandidateDetailModal'
import { RoleAssignmentModal } from '@/components/candidate/RoleAssignmentModal'
import type { Candidate, FactionDemandDetails } from '@/types'
import { ActionsPanel } from '@/components/party/ActionsPanel'
import { PollingPanel } from '@/components/party/PollingPanel'
import { PartyStrengthSummary } from '@/components/party/PartyStrengthSummary'
import { PartyIdeologyMap } from '@/components/party/PartyIdeologyMap'
import { FactionsSection } from '@/components/party/FactionsSection'
import { ActionHistory } from '@/components/party/ActionHistory'
import { FactionDetail } from '@/components/faction/FactionDetail'
import { FactionDemandAlert } from '@/components/faction/FactionDemandAlert'
import PartyProfile from '@/pages/PartyProfile'

export default function PartyPage() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const { party, isLoading, error } = usePartyWithDetails(id)
  const { districtStates, isLoading: districtStatesLoading } =
    usePartyDistrictStatesWithDistricts(id)
  const { leader, isLoading: leaderLoading } = usePartyLeader(id)
  const { candidates } = useCandidatesByParty(id)
  const { world } = useWorld(party?.nation?.world_id)
  const { factions } = useFactionsByParty(id)
  const { election: lastElection } = useLastCompletedElection(party?.nation?.id)
  const { election: upcomingElection } = useUpcomingElection(party?.nation?.id)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [recruitmentMessage, setRecruitmentMessage] = useState<string | null>(null)
  const [leaderDetailModalOpen, setLeaderDetailModalOpen] = useState(false)
  const [roleAssignmentCandidateId, setRoleAssignmentCandidateId] = useState<string | null>(null)
  const [selectedFactionId, setSelectedFactionId] = useState<string | null>(null)

  const handleRecruited = (candidate: Candidate) => {
    setRecruitmentMessage(`${candidate.name} has joined your party!`)
    setTimeout(() => setRecruitmentMessage(null), 3000)
  }

  const handleAssignRoleFromRecruitment = (candidate: Candidate) => {
    setRoleAssignmentCandidateId(candidate.id)
  }

  const handleLeaderChangeRole = (candidateId: string) => {
    setLeaderDetailModalOpen(false)
    setRoleAssignmentCandidateId(candidateId)
  }

  const handleGoToCandidates = () => {
    // Scroll to the candidate list section
    const candidateSection = document.querySelector('[data-section="candidates"]')
    candidateSection?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleGoToRecruitment = () => {
    const recruitmentSection = document.getElementById('recruitment')
    recruitmentSection?.scrollIntoView({ behavior: 'smooth' })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto max-w-4xl py-8 px-4">
          <div className="space-y-6 animate-pulse">
            <div className="h-8 bg-muted rounded w-48" />
            <div className="h-24 bg-muted rounded" />
            <div className="h-20 bg-muted rounded" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="h-48 bg-muted rounded" />
              <div className="h-48 bg-muted rounded" />
              <div className="h-48 bg-muted rounded" />
              <div className="h-48 bg-muted rounded" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto max-w-4xl py-8 px-4">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold text-destructive mb-2">
              Error Loading Party
            </h1>
            <p className="text-muted-foreground">{error.message}</p>
          </div>
        </div>
      </div>
    )
  }

  if (!party) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto max-w-4xl py-8 px-4">
          <div className="text-center py-12">
            <h1 className="text-2xl font-bold mb-2">Party Not Found</h1>
            <p className="text-muted-foreground">
              The party you're looking for doesn't exist.
            </p>
          </div>
        </div>
      </div>
    )
  }

  const isOwner = user?.id === party.user_id

  // Non-owners see the public profile
  if (!isOwner) {
    return <PartyProfile party={party} />
  }

  // Owners see the full dashboard
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto max-w-4xl py-8 px-4">
        <div className="space-y-6">
          {/* Header */}
          <PartyHeader
            party={party}
            nationName={party.nation.name}
            nationId={party.nation.id}
            isOwner={isOwner}
            onEdit={() => setIsEditModalOpen(true)}
          />

          {/* Resources Bar */}
          <ResourcesBar
            money={party.money}
            actionPoints={party.action_points}
            prestige={party.prestige}
            momentum={party.momentum}
          />

          {/* Tick Status Bar */}
          {world && (
            <TickStatus
              worldId={world.id}
              nationId={party.nation.id}
              variant="compact"
              showElectionCountdown
            />
          )}

          {/* Election Countdown */}
          {world && party?.nation && (
            <ElectionCountdown
              currentTick={world.current_tick}
              nextElectionTick={party.nation.next_election_tick}
              termLengthTicks={party.nation.term_length_ticks}
              campaignStartTick={upcomingElection?.campaign_start_tick}
              electionStatus={upcomingElection?.status ?? (lastElection?.status === 'completed' ? 'completed' : null)}
              latestCompletedElectionId={lastElection?.id}
              nationId={party.nation.id}
            />
          )}

          {/* Success message */}
          {recruitmentMessage && (
            <div className="bg-green-100 dark:bg-green-900/30 border border-green-500 text-green-700 dark:text-green-400 px-4 py-2 rounded">
              {recruitmentMessage}
            </div>
          )}

          {/* Faction Demand Alerts - Show urgent demands at the top */}
          {factions && factions.filter(f => f.demand_type && (f.status === 'demanding' || f.status === 'threatening')).length > 0 && (
            <div className="space-y-2">
              {factions
                .filter(f => f.demand_type && (f.status === 'demanding' || f.status === 'threatening'))
                .sort((a, b) => {
                  // Threatening first, then by satisfaction (lower = more urgent)
                  if (a.status === 'threatening' && b.status !== 'threatening') return -1
                  if (b.status === 'threatening' && a.status !== 'threatening') return 1
                  return a.satisfaction - b.satisfaction
                })
                .slice(0, 2) // Show max 2 alerts
                .map(faction => (
                  <FactionDemandAlert
                    key={faction.id}
                    faction={faction}
                    demandDetails={faction.demand_details as unknown as FactionDemandDetails}
                    currentTick={world?.current_tick ?? 0}
                    onViewFaction={() => setSelectedFactionId(faction.id)}
                    variant="compact"
                  />
                ))}
            </div>
          )}

          {/* Party Leader Section - Prominent display */}
          {!leaderLoading && (
            leader ? (
              <PartyLeaderCard
                leader={leader}
                partyIdeology={party.ideology_vector}
                onViewDetails={() => setLeaderDetailModalOpen(true)}
              />
            ) : (
              <NoLeaderWarning
                hasCandidates={(candidates?.length ?? 0) > 0}
                onGoToCandidates={handleGoToCandidates}
                onGoToRecruitment={handleGoToRecruitment}
              />
            )
          )}

          {/* Main content grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Ideology Section */}
            <PartyIdeologySection ideology={party.ideology_vector} />

            {/* District Strength Summary */}
            <PartyStrengthSummary
              partyId={party.id}
              districtStates={districtStates || []}
              isLoading={districtStatesLoading}
            />

            {/* Actions Panel */}
            {world && (
              <ActionsPanel
                partyId={party.id}
                worldId={world.id}
                tickIntervalHours={world.tick_interval_hours}
              />
            )}

            {/* Polling Panel */}
            <PollingPanel
              partyId={party.id}
              currentTick={world?.current_tick ?? 0}
            />

            {/* Factions Section */}
            <FactionsSection
              partyId={party.id}
              onSelectFaction={setSelectedFactionId}
            />
          </div>

          {/* Action History - Full width */}
          <ActionHistory partyId={party.id} />

          {/* Party Ideology Map - Full width */}
          <PartyIdeologyMap
            party={party}
            candidates={candidates || []}
            partyLeaderId={leader?.id}
            isOwner={isOwner}
          />

          {/* Candidate List - Full width */}
          <div data-section="candidates">
            <CandidateList
              partyId={party.id}
              nationId={party.nation.id}
              partyIdeology={party.ideology_vector}
              isOwner={isOwner}
            />
          </div>

          {/* Recruitment Panel - Full width */}
          <div id="recruitment">
            <RecruitmentPanel
              partyId={party.id}
              nationId={party.nation.id}
              partyIdeology={party.ideology_vector}
              currentTick={world?.current_tick ?? 0}
              onRecruited={handleRecruited}
              onAssignRole={handleAssignRoleFromRecruitment}
            />
          </div>
        </div>

        {/* Edit Modal */}
        <EditPartyModal
          party={party}
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
        />

        {/* Leader Detail Modal */}
        {leader && (
          <CandidateDetailModal
            candidateId={leader.id}
            isOpen={leaderDetailModalOpen}
            onClose={() => setLeaderDetailModalOpen(false)}
            partyIdeology={party.ideology_vector}
            isOwner={isOwner}
            onChangeRole={isOwner ? handleLeaderChangeRole : undefined}
          />
        )}

        {/* Role Assignment Modal (for leader role changes) */}
        <RoleAssignmentModal
          candidateId={roleAssignmentCandidateId}
          partyId={party.id}
          nationId={party.nation.id}
          isOpen={roleAssignmentCandidateId !== null}
          onClose={() => setRoleAssignmentCandidateId(null)}
          onSuccess={() => setRoleAssignmentCandidateId(null)}
        />

        {/* Faction Detail Modal */}
        {selectedFactionId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <FactionDetail
                factionId={selectedFactionId}
                onClose={() => setSelectedFactionId(null)}
                editable={true}
                currentTick={world?.current_tick ?? 0}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
